import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of, Subject} from "rxjs";
import {HttpClient, HttpErrorResponse, HttpParams} from "@angular/common/http";
import {catchError, delay, switchMap} from "rxjs/operators";
import {environment} from "../../../environments/environment";
import {IUser, IUserPayoutLock} from "../../@core/interfaces/user/user.interface";
import {
  IUpdateUserLockItem,
  IUserLocks,
  IUserLockHistoryMetadata,
  LockTypeEnum,
} from "../../@core/interfaces/user/user-lock.interface";
import {IAuthLogs} from "../../@modules/+search-user/modals/auth-history/auth-history.interface";
import {SnackBarService} from "../../@core/services/snack-bar.service";
import {Router} from "@angular/router";
import {
  IReferralStatFilter,
  IReferralListFilter,
  IReferralListPage,
  IReferralsStat,
  IRefIncome
} from "../../@modules/+search-user/user-content/services/interfaces/referral-campaigns.interface";
import {ICommonStat} from "../../@modules/+search-user/user-content/services/interfaces/common-stats.interface";
import {
  IFreeCase,
  IFreeCasesLog
} from "../../@modules/+search-user/user-content/services/interfaces/free-cases.interface";
import {
  IWelcomeCaseProgress
} from "../../@modules/+search-user/user-content/services/interfaces/welcome-case-progress.interface";
import {IUserWallet} from "../../@modules/+search-user/user-content/services/interfaces/user-wallet.interface";
import {
  IBalanceHistoryBody,
  IBalanceHistoryResponse
} from "../../@modules/+search-user/user-content/services/interfaces/balance-history.interface";
import {IPayback, IPaybacksMetadata, IUserPayback} from "../../@core/enums/payback/interfaces/payback.interface";
import {ICreateOrUpdatePayback} from "../../@core/enums/payback/interfaces/create-or-update-payback.interface";
import {
  IGetPaybackWallets,
  IPaybackWalletsMetadata
} from "../../@core/enums/payback/interfaces/payback-wallet/get-payback-wallets.interface";
import {IPaybackWallet} from "../../@core/enums/payback/interfaces/payback-wallet/payback-wallet.interface";
import {IChangePaybackWallet} from "../../@core/enums/payback/interfaces/payback-wallet/change-payback-wallet.interface";
import {IUserContact} from "../../@core/interfaces/user/user-contact.interface";

@Injectable({
  providedIn: 'root'
})
export class UserService {

  public currentUserId: BehaviorSubject<number> = new BehaviorSubject<number>(1);

  private readonly userRequest: Subject<number> = new Subject<number>();
  private readonly user: BehaviorSubject<IUser | null> = new BehaviorSubject<IUser | null>(null);
  public readonly user$: Observable<IUser | null> = this.user.asObservable();

  private readonly commonStatRequest: Subject<any> = new Subject<any>();
  private readonly commonStat: Subject<ICommonStat> = new Subject<ICommonStat>();
  public readonly commonStat$: Observable<ICommonStat> = this.commonStat.asObservable();


  private readonly refIncomeRequest: Subject<{ pageSize: number, currentPage: number }> = new Subject<{
    pageSize: number,
    currentPage: number
  }>();
  private readonly refIncome: Subject<IRefIncome> = new Subject<IRefIncome>();
  public readonly refIncome$: Observable<IRefIncome> = this.refIncome.asObservable();

  private readonly authLogsRequest: Subject<void> = new Subject<void>();
  private readonly authLogs: BehaviorSubject<IAuthLogs[]> = new BehaviorSubject<any>(null);
  public readonly authLogs$: Observable<IAuthLogs[]> = this.authLogs.asObservable();

  private readonly checkInGameRequest: Subject<void> = new Subject<void>();
  private readonly checkInGame: BehaviorSubject<{ inGame: boolean; list: string[] }> = new BehaviorSubject<any>(null);
  public readonly checkInGame$: Observable<{ inGame: boolean; list: string[] }> = this.checkInGame.asObservable();

  private readonly freeCasesLogRequest: Subject<void> = new Subject<void>();
  private readonly freeCasesLog: Subject<IFreeCasesLog[]> = new Subject<IFreeCasesLog[]>()
  public readonly freeCasesLog$: Observable<IFreeCasesLog[]> = this.freeCasesLog.asObservable();

  constructor(private readonly http: HttpClient,
              private router: Router,
              private sb: SnackBarService) {
    this.subscribeEmitters();
  }

  public requestUser(): void {
    this.userRequest.next(this.currentUserId.getValue());
  }

  public resetUser(): void {
    this.user.next(null);
  }

  public requestCommonStat(filter: any): void {
    this.commonStatRequest.next(filter)
  }

  public getReferralSummary(filter: IReferralStatFilter): Observable<IReferralsStat> {
    let params = new HttpParams()
      .set('dateFrom', filter.start).set('dateTo', filter.end)
      .set('referredInPeriodOnly', String(filter.referredInPeriodOnly ?? false));
    const userId = filter.userId ?? this.user.getValue()?.fastId;
    if (userId != null) params = params.set('userId', String(userId));
    if (filter.referralCampaignId != null) params = params.set('referralCampaignId', String(filter.referralCampaignId));
    if (filter.code) params = params.set('code', filter.code);
    return this.http.get<IReferralsStat>(
      `${environment.GATEWAY_URL}/referral/stats`,
      {params, withCredentials: true},
    );
  }

  public getReferralAdminReferrals(filter: IReferralListFilter): Observable<IReferralListPage> {
    let params = new HttpParams()
      .set('dateFrom', filter.start).set('dateTo', filter.end)
      .set('referredInPeriodOnly', String(filter.referredInPeriodOnly ?? false))
      .set('limit', String(filter.limit)).set('offset', String(filter.offset))
      .set('filter', filter.filter);
    if (filter.sortBy) params = params.set('sortBy', filter.sortBy);
    const userId = filter.userId ?? this.user.getValue()?.fastId;
    if (userId != null) params = params.set('userId', String(userId));
    if (filter.referralCampaignId != null) params = params.set('referralCampaignId', String(filter.referralCampaignId));
    if (filter.code) params = params.set('code', filter.code);
    return this.http.get<IReferralListPage>(
      `${environment.GATEWAY_URL}/referral/stats/referrals`,
      {params, withCredentials: true},
    );
  }


  public requestRefIncome(pageSize: number, currentPage: number): void {
    this.refIncomeRequest.next({pageSize, currentPage})
  }

  public requestAuthLog(): void {
    this.authLogsRequest.next();
  }

  public requestCheckInGame(): void {
    this.checkInGameRequest.next();
  }

  public requestFreeCasesLog(): void {
    this.freeCasesLogRequest.next();
  }

  checkUserSteam(): Observable<ICommonStat> {
    return this.http.get<any>(`${environment.GATEWAY_URL}/market/steam/check/${this.currentUserId.getValue()}`)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  getUserCommonStats(): Observable<ICommonStat> {
    return this.http.post<any>(`${environment.GATEWAY_URL}/user/common-statistics`, {
      "userId": this.currentUserId.getValue(),
      "dateFrom": new Date('2014-01-01'),
      "dateTo": new Date()
    })
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  getUserInventory(): Observable<any> {
    return this.http.get<any>(`${environment.GATEWAY_URL}/inventory/${this.currentUserId.getValue()}`)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  resetUserInventory(): Observable<void> {
    return this.http.post<void>(
      `${environment.GATEWAY_URL}/inventory/${this.currentUserId.getValue()}/reset`,
      {}
    ).pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  getUserRecentOperations(pageSize: number, pageNumber: number, sourceType: any, filterKey: number[] = [], walletTypeIds: number[] = []): Observable<any> {
    const params = new HttpParams()
      .set('page[size]', pageSize.toString())
      .set('page[number]', pageNumber.toString())
      .set('source', sourceType)
      .set('keys', filterKey.join(','))
      .set('walletTypeIds', walletTypeIds.join(','));

    return this.http.get<any>(`${environment.GATEWAY_URL}/user/${this.currentUserId.getValue()}/transactions-history-v2`, {params})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  getInventoryOperations(value: any): Observable<any> {
    return this.http.get<any>(`${environment.GATEWAY_URL}/inventory/${this.currentUserId.getValue()}/logs`, {
      params: {
        "limit": Number(value.limit),
        "agentKeys": value.filter_key,
      }
    })
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  getUserWallets(): Observable<IUserWallet[]> {
    // return of([
    //     {
    //       "id": 21,
    //       "balance": 0,
    //       "walletType": {
    //         "id": 500
    //       }
    //     },
    //     {
    //       "id": 852,
    //       "balance": 1809945711,
    //       "walletType": {
    //         "id": 3
    //       }
    //     },
    //     {
    //       "id": 1096,
    //       "balance": 400,
    //       "walletType": {
    //         "id": 4
    //       }
    //     },
    //     {
    //       "id": 23,
    //       "balance": 2147483300,
    //       "walletType": {
    //         "id": 501
    //       }
    //     }
    //   ]).pipe(delay(1000))
    return this.http.get<IUserWallet[]>(`${environment.GATEWAY_URL}/wallet/wallets?userId=${this.currentUserId.getValue()}`)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  getUserWalletTransactions(body: IBalanceHistoryBody | undefined, page: {
    size: number;
    number: number
  }): Observable<IBalanceHistoryResponse> {
    return this.http.post<IBalanceHistoryResponse>(`${environment.GATEWAY_URL}/wallet/transactions`, {
      userId: this.currentUserId.getValue(),
      ...body,
      page
    })
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  getUserWithdrawals(): Observable<any> {
    return this.http.get<any>(`${environment.GATEWAY_URL}/stats/withdraws/${this.currentUserId.getValue()}`)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  getUserRefills(): Observable<any> {
    return this.http.get<any>(`${environment.GATEWAY_URL}/user/refills?userId=${this.currentUserId.getValue()}`)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  getUserReferrals(): Observable<any> {
    return this.http.post<any>(`${environment.GATEWAY_URL}/user/referrals`, {
      "userId": this.currentUserId.getValue(),
    })
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  getReferralCampaigns(id: any = null): Observable<any> {
    return this.http.get<any>(`${environment.GATEWAY_URL}/referral/${id || this.currentUserId.getValue()}`)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public resetAllIncomeFromTarget(targetId: number, data: any): Observable<any> {
    const body = {
      ...data,
      referrerUserId: this.currentUserId.getValue()
    }
    return this.http.post<any>(`${environment.GATEWAY_URL}/referral/${this.currentUserId.getValue()}/reset-income/${targetId}`, body, {withCredentials: true})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public resetAllIncome(data: any): Observable<any> {
    return this.http.post<any>(`${environment.GATEWAY_URL}/referral/${this.currentUserId.getValue()}/reset-income`, data, {withCredentials: true})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public lockAllUserRefs(): Observable<any> {
    return this.http.get<any>(`${environment.GATEWAY_URL}/referral/${this.currentUserId.getValue()}/ref-ban`, {withCredentials: true})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  /**
   * Принудительный вывод реф-дохода (FAST_NEW_BACK-2267): reward_coins + reward_coins_moderated → reward_coins_taken
   * и зачисление на баланс. Ответ — сводный доход владельца после вывода (блок «Доход»).
   */
  public forceWithdrawReferralIncome(): Observable<any> {
    return this.http.post<any>(`${environment.GATEWAY_URL}/referral/${this.currentUserId.getValue()}/force-withdraw`, {}, {withCredentials: true})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  /** Обнуление долга рефовода (FAST_NEW_BACK-2269): отрицательный reward_coins всех его кампаний → 0. */
  public clearReferralCampaignDebt(): Observable<any> {
    return this.http.post<any>(`${environment.GATEWAY_URL}/referral/clear-debt`, {userId: this.currentUserId.getValue()}, {withCredentials: true})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  addMoney(body: any): Observable<any> {
    return this.http.post<any>(`${environment.GATEWAY_URL}/user/${this.currentUserId.getValue()}/prvt/add-money`, body)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  addDemoMoney(amount: number, comment: string): Observable<any> {
    return this.http.post<any>(`${environment.GATEWAY_URL}/user/${this.currentUserId.getValue()}/prvt/add-demo-money`, {
      amount,
      comment
    })
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  unlockBalance(): Observable<any> {
    return this.http.post<any>(`${environment.GATEWAY_URL}/user/${this.currentUserId.getValue()}/unlock`, {})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  toggleVerify(checked: boolean): Observable<any> {
    return this.http.patch<any>(`${environment.GATEWAY_URL}/user/verify`, {
      "userId": this.currentUserId.getValue(),
      "verify": checked
    })
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  // Единый эндпоинт изменения блокировок (батч locks[]). Заменил дедикейтед-тогглы.
  updateUserLocks(locks: IUpdateUserLockItem[]): Observable<IUserLocks> {
    return this.http.post<IUserLocks>(
      `${environment.GATEWAY_URL}/user/${this.currentUserId.getValue()}/locks`,
      {locks})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  // Хелпер: переключить один тип блокировки.
  toggleUserLock(type: LockTypeEnum, isActive: boolean, reason: string, expiresAt: string | null = null): Observable<IUserLocks> {
    return this.updateUserLocks([{type, isActive, reason, expiresAt}]);
  }

  // Активные блокировки пользователя (список строк с атрибуцией).
  getUserLocks(): Observable<IUserLocks> {
    return this.http.get<IUserLocks>(
      `${environment.GATEWAY_URL}/user/${this.currentUserId.getValue()}/locks`)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  /** Получить контакты выбранного пользователя. */
  getUserContacts(includeRevoked: boolean = false): Observable<IUserContact[]> {
    const params = new HttpParams().set('includeRevoked', String(includeRevoked));

    return this.http.get<IUserContact[]>(
      `${environment.GATEWAY_URL}/user/${this.currentUserId.getValue()}/contacts`,
      {params},
    );
  }

  // Журнал блокировок пользователя.
  getUserLockHistory(page = 1, size = 20): Observable<IUserLockHistoryMetadata> {
    return this.http.get<IUserLockHistoryMetadata>(
      `${environment.GATEWAY_URL}/user/${this.currentUserId.getValue()}/lock-history?page[number]=${page}&page[size]=${size}`)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  createOrUpdateUserPayoutLock(body: IUserPayoutLock): Observable<any> {
    return this.http.post<any>(`${environment.GATEWAY_URL}/user/${this.currentUserId.getValue()}/payout-lock-settings`, body)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  toggleSetPromoMoreDeposit(checked: boolean, reason: string): Observable<any> {
    return this.http.patch<any>(`${environment.GATEWAY_URL}/user/${this.currentUserId.getValue()}/promo-more-deposit/${checked}`,
      {reason})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  getUserWelcomeCases(): Observable<IWelcomeCaseProgress[]> {
    return this.http.get<IWelcomeCaseProgress[]>(`${environment.GATEWAY_URL}/user/${this.currentUserId.getValue()}/welcome-case-progress`, {withCredentials: true})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  toggleTrader(checked: boolean) {
    return this.http.patch<any>(`${environment.GATEWAY_URL}/user/trader`, {
      "userId": this.currentUserId.getValue(),
      "verify": checked
    })
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  toggleIgnoreFraud(checked: boolean) {
    return this.http.patch<any>(`${environment.GATEWAY_URL}/user/ignore-fraud`, {
      "userId": this.currentUserId.getValue(),
      "checked": checked
    })
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public resetFaucet(): Observable<any> {
    return this.http.get<any>(`${environment.GATEWAY_URL}/user/${this.currentUserId.getValue()}/reset-faucet`)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public recalculateAccountType(): Observable<any> {
    return this.http.get<any>(`${environment.GATEWAY_URL}/user/${this.currentUserId.getValue()}/recalculate-type`)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public recalculateAccountTypeWithDowngrade(): Observable<any> {
    return this.http.get<any>(`${environment.GATEWAY_URL}/user/${this.currentUserId.getValue()}/recalculate-type-downgrade`)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public changeUserRefCode(code: string): Observable<any> {
    return this.http.post<any>(`${environment.GATEWAY_URL}/referral/${this.currentUserId.getValue()}/referral-codes/${code}`, {}, {withCredentials: true})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public changeUserName(nickname: string): Observable<any> {
    return this.http.patch<any>(`${environment.GATEWAY_URL}/user/${this.currentUserId.getValue()}/set-nickname`, {nickname}, {withCredentials: true})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public resetUserAvatar(): Observable<any> {
    return this.http.post<any>(`${environment.GATEWAY_URL}/user/${this.currentUserId.getValue()}/reset-avatar`, {}, {withCredentials: true})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public getAllFreeCases(): Observable<IFreeCase[]> {
    return this.http.get<IFreeCase[]>(`${environment.GATEWAY_URL}/cases`)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public addUserFreeCase(body: { count: number, freeCaseId: number }): Observable<any> {
    return this.http.post(`${environment.GATEWAY_URL}/cases/${this.currentUserId.getValue()}/add`, body, {withCredentials: true})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public getPaybacks(): Observable<IUserPayback[]> {
    return this.http.post<IUserPayback[]>(`${environment.GATEWAY_URL}/user/payback`, {
      userId: this.currentUserId.getValue()
    }, {withCredentials: true})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public createOrUpdatePayback(body: ICreateOrUpdatePayback): Observable<IPayback> {
    return this.http.post<IPayback>(`${environment.GATEWAY_URL}/user/create-or-update-payback`, body, {withCredentials: true})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public getPaybackWallets(body: IGetPaybackWallets): Observable<IPaybackWalletsMetadata> {
    return this.http.post<IPaybackWalletsMetadata>(`${environment.GATEWAY_URL}/user/payback-wallets`, body, {withCredentials: true})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public changePaybackWallet(body: IChangePaybackWallet): Observable<IPaybackWallet> {
    return this.http.patch<IPaybackWallet>(`${environment.GATEWAY_URL}/user/payback-wallets`, body, {withCredentials: true})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public resetUserAccount(fastId: number): Observable<any> {
    return this.http.get<any>(`${environment.GATEWAY_URL}/user/reset-account/${fastId}`)
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  public rotateProvablyFairSeed(fastId: number): Observable<any> {
    return this.http.post<any>(`${environment.GATEWAY_URL}/user/${fastId}/rotate-provably-fair-seed`, {})
      .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)));
  }

  private subscribeEmitters(): void {
    this.userRequest.pipe(
      switchMap((userId: number) => this.http.get(`${environment.GATEWAY_URL}/user/${userId}`, {withCredentials: true})
        .pipe(catchError((error: HttpErrorResponse) => {
          this.router.navigate(['/'])
          return this.onHandleError(error)
        }))),
    ).subscribe((user: IUser) => this.user.next(user));

    this.commonStatRequest.pipe(
      switchMap((filter: any) => {
        return this.http.post(`${environment.GATEWAY_URL}/user/common-statistics`, {
          "userId": this.user.getValue()?.fastId,
          "dateFrom": filter.start,
          "dateTo": filter.end,
          "extended": true,
        }, {withCredentials: true})
          .pipe(catchError((error: HttpErrorResponse) => {
            return this.onHandleError(error)
          }))
      }),
    ).subscribe((commonStat: ICommonStat) => this.commonStat.next(commonStat));


    this.refIncomeRequest.pipe(
      switchMap((params: { pageSize: number, currentPage: number }) => {
        return this.http.get(`${environment.GATEWAY_URL}/referral/${this.user.getValue()?.fastId}/ref-income?page[size]=${params.pageSize}&page[number]=${params.currentPage + 1}`, {withCredentials: true})
          .pipe(catchError((error: HttpErrorResponse) => {
            return this.onHandleError(error)
          }))
      }),
    ).subscribe((referralStat: any) => this.refIncome.next(referralStat));

    this.authLogsRequest.pipe(
      switchMap(() => this.http.get(`${environment.GATEWAY_URL}/user/${this.currentUserId.getValue()}/auth-logs`, {withCredentials: true})
        .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)))),
    ).subscribe(
      (result: any) => this.authLogs.next(result),
    );

    this.checkInGameRequest.pipe(
      switchMap(() => this.http.get(`${environment.GATEWAY_URL}/user/${this.currentUserId.getValue()}/check-in-game`, {withCredentials: true})
        .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)))),
    ).subscribe(
      (result: any) => this.checkInGame.next(result),
    );

    this.freeCasesLogRequest.pipe(
      switchMap(() => this.http.get(`${environment.GATEWAY_URL}/user/${this.currentUserId.getValue()}/free-cases`, {withCredentials: true})
        .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)))),
    ).subscribe(
      (result: IFreeCasesLog[]) => this.freeCasesLog.next(result),
    );
  }

  private onHandleError(error: HttpErrorResponse): Observable<any> {
    const message = error.error.statusCode + ' ' + (error.error.message || error.error.error);
    this.sb.openSnackBar(message, 3000, 'error');
    return of();
  }
}
